import { useEffect, useRef, useState } from "react"

const WORDS = ["guns.", "ammo.", "gun parts.", "accessories.", "optics."]

const LONGEST_WORD = WORDS.reduce((longest, word) =>
    word.length > longest.length ? word : longest
)

/**
 * The hold is fixed per word, so long and short words get the same reading
 * time. Resuming after the pause control waits only RESUME_MS.
 */
const HOLD_MS = 3000
const DELETE_MS = 40
const GAP_MS = 150
const TYPE_MS = 75
const RESUME_MS = 400

const FIRST_WORD = { phase: "hold", word: 0, typed: WORDS[0].length }

/**
 * The word after "We also buy". The server renders "guns." fully typed, so the
 * sentence is complete before and without JavaScript, and the invisible copy
 * of the longest word holds the block's width and height while words cycle.
 *
 * Screen readers get the whole list once from the visually hidden sentence;
 * the typing itself is aria-hidden and nothing here is aria-live.
 */
export const HeroRotatingWord = ({ isPaused }) => {
    const [display, setDisplay] = useState({ word: 0, typed: FIRST_WORD.typed })
    const [prefersReducedMotion, setPrefersReducedMotion] = useState(false)
    const machine = useRef({ ...FIRST_WORD })
    const hasStarted = useRef(false)

    useEffect(() => {
        const query = window.matchMedia("(prefers-reduced-motion: reduce)")
        const sync = () => setPrefersReducedMotion(query.matches)

        sync()
        query.addEventListener("change", sync)

        return () => query.removeEventListener("change", sync)
    }, [])

    useEffect(() => {
        if (prefersReducedMotion) {
            machine.current = { ...FIRST_WORD }
            hasStarted.current = false
            setDisplay({ word: 0, typed: FIRST_WORD.typed })

            return
        }

        if (isPaused) return

        const state = machine.current
        let timer

        const schedule = (ms) => {
            timer = setTimeout(step, ms)
        }

        const step = () => {
            if (state.phase === "hold") {
                state.phase = "delete"
            }

            if (state.phase === "delete") {
                if (state.typed > 0) {
                    state.typed -= 1
                    setDisplay({ word: state.word, typed: state.typed })

                    return schedule(DELETE_MS)
                }

                state.phase = "type"
                state.word = (state.word + 1) % WORDS.length
                setDisplay({ word: state.word, typed: 0 })

                return schedule(GAP_MS)
            }

            if (state.typed < WORDS[state.word].length) {
                state.typed += 1
                setDisplay({ word: state.word, typed: state.typed })

                return schedule(TYPE_MS)
            }

            state.phase = "hold"
            schedule(HOLD_MS)
        }

        schedule(hasStarted.current ? RESUME_MS : HOLD_MS)
        hasStarted.current = true

        return () => clearTimeout(timer)
    }, [isPaused, prefersReducedMotion])

    return (
        <span className="falh-rot">
            <span className="falh-rot-ghost" aria-hidden="true">
                {LONGEST_WORD}
            </span>
            <span className="falh-rot-word" aria-hidden="true">
                {WORDS[display.word].slice(0, display.typed)}
                <span className="falh-cursor" />
            </span>
            <span className="sr-only">
                guns, ammo, gun parts, accessories and optics.
            </span>
        </span>
    )
}
